import { cn } from '@/lib/utils';
import { Button } from '@ui/button';
import { Card, CardContent } from '@ui/authcard';
import { Input } from '@ui/input';
import { Label } from '@ui/label';
// import { Tooltip, TooltipTrigger, TooltipContent } from '@ui/tooltip';
// import { ChevronDownIcon } from 'lucide-react';
import { useState, useEffect } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowTurnBackwardIcon } from '@hugeicons/core-free-icons';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useAuthDialog } from '../../contexts/AuthDialogContext';
import { useToast } from '../../contexts/ToastContext';
import { useTranslation } from 'react-i18next';
import { logger } from '../../utils/logger';
// Phone validation imports - commented out for now
// import {
//   validateMyanmarPhoneNumber,
//   formatPhoneNumberDisplay,
//   toApiFormat,
// } from '../../utils/phoneValidation';

interface ForgetPasswordFormProps extends React.ComponentProps<'div'> {
  onSuccess?: () => void;
  onStepChange?: (step: number) => void;
}

export function ForgetPasswordForm({
  className,
  onSuccess,
  onStepChange,
  ...props
}: ForgetPasswordFormProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    // phoneNumber: '', // Commented out for now
    otp: '',
    password: '',
    verifyPassword: '',
  });
  const [countdown, setCountdown] = useState(5 * 60); // 5 minutes in seconds
  const [canResend, setCanResend] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showVerifyPassword, setShowVerifyPassword] = useState(false);
  // const [phoneValidationError, setPhoneValidationError] = useState('');
  // const [showPhoneValidation, setShowPhoneValidation] = useState(false);
  const { forgotPassword, resetPassword } = useAuth();
  const { showAuthDialog } = useAuthDialog();
  const { showToast } = useToast();

  // Check URL parameters for OTP and email on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const otpFromUrl = urlParams.get('otp');
    const emailFromUrl = urlParams.get('email');
    const pageParam = urlParams.get('pg');

    if (otpFromUrl && emailFromUrl && pageParam === 'reset-password') {
      setFormData((prev) => ({
        ...prev,
        otp: otpFromUrl,
        email: emailFromUrl,
      }));
      setStep(3); // Skip to password reset step
    }
  }, []);

  // Notify parent component when step changes
  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (step === 2 && countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [step, countdown]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Phone number validation - commented out for now
    // if (name === 'phoneNumber') {
    //   const formattedValue = formatPhoneNumberDisplay(value);

    //   // Validate the phone number
    //   if (value.trim()) {
    //     const validation = validateMyanmarPhoneNumber(value);
    //     if (!validation.isValid) {
    //       setPhoneValidationError(validation.error || 'Invalid phone number');
    //     } else {
    //       setPhoneValidationError('');
    //     }
    //   } else {
    //     setPhoneValidationError('');
    //   }

    //   setFormData((prev) => ({
    //     ...prev,
    //     [name]: formattedValue,
    //   }));
    // } else {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (step === 1) {
      // Validate email
      if (!formData.email.trim()) {
        setError('Please enter your email address');
        return;
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError('Please enter a valid email address');
        return;
      }
    } else if (step === 2) {
      // Validate OTP
      if (!formData.otp || formData.otp.length !== 6) {
        setError('Please enter the 6-digit verification code');
        return;
      }
    } else if (step === 3) {
      // Validate passwords
      if (!formData.password.trim()) {
        setError('Please enter a new password');
        return;
      }
      if (!formData.verifyPassword.trim()) {
        setError('Please confirm your password');
        return;
      }
      if (formData.password !== formData.verifyPassword) {
        setError('Passwords do not match');
        return;
      }
    }

    handleNextStep(e);
  };

  const handleNextStep = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      if (step === 1 && formData.email) {
        // Request password reset OTP via email
        const response = await forgotPassword({
          email: formData.email,
          activeRole: 'user',
        });

        if (response.success) {
          if (response.message) {
            showToast('success', response.message);
          }
          setStep(2);
          // Don't close dialog here, keep it open for OTP input
        } else {
          if (response.error) {
            setError(response.error);
            showToast('error', response.error);
          }
        }
      } else if (step === 2 && formData.otp) {
        // Move to password reset step (OTP will be verified in final step)
        setStep(3);
      } else if (step === 3 && formData.password && formData.verifyPassword) {
        // Complete password reset
        if (formData.password !== formData.verifyPassword) {
          setError('Passwords do not match.');
          return;
        }

        const response = await resetPassword({
          email: formData.email,
          activeRole: 'user',
          otp: formData.otp,
          newPassword: formData.password,
        });

        if (response.success) {
          // Password reset successful, call success callback or show auth dialog
          if (response.message) {
            showToast('success', response.message);
          }
          if (onSuccess) {
            onSuccess();
          } else {
            showAuthDialog('login');
          }
        } else {
          if (response.error) {
            setError(response.error);
            showToast('error', response.error);
          }
        }
      }
    } catch (error) {
      logger.error('Password reset error:', error);
      if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof error.message === 'string'
      ) {
        setError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrevStep = () => {
    setStep((prev) => prev - 1);
  };

  const handleResendOTP = async () => {
    setIsLoading(true);
    try {
      // API call to resend OTP
      const response = await forgotPassword({
        email: formData.email,
        activeRole: 'user',
      });

      if (response.success) {
        setCountdown(5 * 60); // 5 minutes in seconds
        setCanResend(false);
        setError('');
        if (response.message) {
          showToast('success', response.message);
        }
        // Don't close dialog here, keep it open for OTP input
      } else {
        if (response.error) {
          setError(response.error);
        }
      }
    } catch (error) {
      logger.error('Resend OTP error:', error);
      if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof error.message === 'string'
      ) {
        setError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getStepContent = () => {
    switch (step) {
      case 1:
        return (
          <>
            {/* Step 1: Email */}
            <div className="grid gap-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="example@example.com"
                className="w-full"
                required
              />
            </div>

            {/* Phone Number - Commented out for now */}
            {/* <div className="grid gap-2">
              <Label htmlFor="phone">{t('auth.phone')}</Label>
              <div className="flex rounded-md relative">
                <div className="grid shrink-0 grid-cols-1 focus-within:relative">
                  <select
                    id="country"
                    name="country"
                    autoComplete="country"
                    aria-label="Country"
                    disabled
                    defaultValue="+95"
                    className="col-start-1 row-start-1 w-full appearance-none rounded-l-md border border-input bg-background py-1.5 pr-7 pl-3 text-sm text-muted-foreground ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  >
                    <option>+95</option>
                  </select>
                  <ChevronDownIcon
                    aria-hidden="true"
                    className="pointer-events-none col-start-1 row-start-1 mr-2 size-4 self-center justify-self-end text-muted-foreground"
                  />
                </div>
                <Tooltip open={!!phoneValidationError}>
                  <TooltipTrigger asChild>
                    <Input
                      id="phoneNumber"
                      name="phoneNumber"
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      placeholder="09 xxx xxxxxx"
                      maxLength={13}
                      className="rounded-l-none border-l-0 focus:border-l focus:ring-offset-0"
                      required
                    />
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <div className="flex items-center gap-2">
                      <span className="text-red-600">⚠</span>
                      <span>{phoneValidationError}</span>
                    </div>
                  </TooltipContent>
                </Tooltip>
                {showPhoneValidation && (
                  <div className="relative">
                    <Tooltip open={showPhoneValidation}>
                      <TooltipTrigger asChild>
                        <div className="absolute inset-0 pointer-events-none" />
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <div className="flex items-center gap-2">
                          <span className="text-orange-600 font-bold">!</span>
                          <span>Please fill out this field.</span>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                )}
              </div>
            </div> */}

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Sending Reset Code...' : 'Continue'}
            </Button>
          </>
        );
      case 2:
        return (
          <>
            {/* Step 2: OTP Verification */}
            <div className="grid text-center gap-4">
              <p className="text-sm text-muted-foreground">
                We've sent a verification code to {formData.email}
              </p>
              <div>
                <div className="grid grid-cols-6 gap-2">
                  {[...Array(6)].map((_, i) => (
                    <Input
                      key={i}
                      type="text"
                      maxLength={1}
                      className="aspect-square text-center text-lg"
                      name={`otp-${i}`}
                      value={formData.otp[i] || ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, ''); // Only allow numbers
                        if (value.length <= 1) {
                          const newOtp = formData.otp.split('');
                          newOtp[i] = value;
                          const updatedOtp = newOtp.join('').slice(0, 6);

                          setFormData((prev) => ({
                            ...prev,
                            otp: updatedOtp,
                          }));

                          // Auto-focus next input
                          if (value.length === 1 && i < 5) {
                            const nextInput = document.querySelector(`input[name=otp-${i + 1}]`);
                            if (nextInput) {
                              (nextInput as HTMLInputElement).focus();
                            }
                          }
                        }
                      }}
                      onKeyDown={(e) => {
                        // Handle backspace to move to previous input
                        if (e.key === 'Backspace' && !formData.otp[i] && i > 0) {
                          const prevInput = document.querySelector(`input[name=otp-${i - 1}]`);
                          if (prevInput) {
                            (prevInput as HTMLInputElement).focus();
                          }
                        }
                      }}
                      required
                    />
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-center text-sm">
                {canResend ? (
                  <Button
                    onClick={handleResendOTP}
                    variant="link"
                    className="p-0 h-auto"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Sending...' : 'Resend OTP'}
                  </Button>
                ) : (
                  <div className="text-muted-foreground cursor-not-allowed">
                    <span className="animate-pulse">Resend OTP in </span>
                    {formatTime(countdown)}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-6 gap-4">
                <Button type="button" variant="outline" className="w-full" onClick={handlePrevStep}>
                  <HugeiconsIcon icon={ArrowTurnBackwardIcon} size={24} className="relative z-10" />
                </Button>
                <Button type="submit" className="w-full col-span-5" disabled={isLoading}>
                  {isLoading ? 'Verifying...' : 'Verify'}
                </Button>
              </div>
            </div>
          </>
        );
      case 3:
        return (
          <>
            {/* Step 3: Password Setup */}
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="password">{t('auth.new_password')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter your new password"
                    className="pr-10"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <div className="grid gap-2 mb-2">
                <Label htmlFor="verifyPassword">{t('auth.verify_password')}</Label>
                <div className="relative">
                  <Input
                    id="verifyPassword"
                    name="verifyPassword"
                    type={showVerifyPassword ? 'text' : 'password'}
                    value={formData.verifyPassword}
                    onChange={handleInputChange}
                    placeholder="Confirm your new password"
                    className="pr-10"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowVerifyPassword(!showVerifyPassword)}
                  >
                    {showVerifyPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-6 gap-4">
                <Button type="button" variant="outline" className="w-full" onClick={handlePrevStep}>
                  <HugeiconsIcon icon={ArrowTurnBackwardIcon} size={24} className="relative z-10" />
                </Button>
                <Button type="submit" className="w-full col-span-5" disabled={isLoading}>
                  {isLoading ? 'Resetting Password...' : 'Reset Password'}
                </Button>
              </div>
            </div>
          </>
        );
    }
  };

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card className="overflow-hidden py-0">
        <CardContent className="p-6">
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-6">
              {/* <div className="flex flex-col items-center text-center">
                <h1 className="text-2xl font-bold">{t('pages.forgot_password')}</h1>
                <p className="text-balance text-muted-foreground">
                  {step === 1 && t('enter_your_email_address')}
                  {step === 2 && t('verify_your_email_address')}
                  {step === 3 && t('create_new_password')}
                </p>
              </div> */}

              {error && (
                <div className="bg-destructive/15 border border-destructive/20 text-destructive px-4 py-3 rounded">
                  {error}
                </div>
              )}

              {getStepContent()}

              {/* Back to Login button removed - handled by AuthDialog */}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
